"use client";

// ── Contest platform hooks (EX-PUB08) ───────────────────────────────────────
// Keys carry the viewer where the same URL answers differently signed in and
// out - the contest page, the problem and the standings all do, because the
// server annotates registration and moderation onto them. Serving one viewer's
// answer to the other is how a signed-out reader ends up looking at a submit
// button the server will refuse.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import * as api from "./api";
import { AUTHORING_KEY, authoringQuery } from "./authoring-query";
import { CONTEST_PROBLEM_CHECK_MAX, isContestSubmissionPending } from "./types";
import type {
  ContestBrandInput,
  EditorSeason,
  SeasonInput,
  ContestSimilarityPairKey,
  ContestStampAddonItem,
  ContestVenueLock,
  ContestVenueLockInput,
} from "./types";
import { pendingPollInterval } from "@/lib/pending-poll";
import {
  announcementsAlerting,
  announcementsLive,
  announcementsSeenKey,
  clarificationsSeenKey,
  newestAnnouncementStamp,
  newestClarificationAnswerStamp,
  readSeenMarker,
  rememberSeenMarker,
  unseenAnnouncements,
  unseenClarificationAnswers,
  type AnnouncementWindow,
} from "./lib/announcement-inbox";
import type { RunSampleInput } from "@/features/judge/types";
import type { RegistrationAnswerInput, RegistrationQuestionInput } from "./registration";
import type { PdfAction } from "@/features/problem-set-pdf/pdf-delivery";
import { usePdfRun, type PdfRun } from "@/features/problem-set-pdf/use-pdf-run";
import type {
  StandingsView,
  AssignContestSeatInput,
  ContestCertificateSettingsInput,
  CreateContestRoomInput,
  UpdateContestTeamInput,
  LiveLayer,
} from "./types";
import type {
  ContestBalloonStatus,
  ContestDirectoryKind,
  ContestDirectoryScope,
  ContestPrintStatus,
  ContestFieldSubmissionListQuery,
  ContestSubmissionListQuery,
  MyContestSubmissionsEverywhereQuery,
  ContestPersonInput,
  ContestStaffInput,
  ContestStaffListRole,
  ContestSubmitInput,
  CreateContestInput,
  ExtendContestInput,
  OrganizerSubmissionsQuery,
  RejudgeInput,
  RequestPrintInput,
  UpdateContestInput,
} from "./types";

export const contestKeys = {
  // The viewer is in the key since 2026-09-26: each card carries whether this
  // reader is registered, so a list read signed out must not serve the one
  // read signed in, and the other way round.
  directory: (
    viewer: string,
    scope: ContestDirectoryScope,
    page: number,
    kind: ContestDirectoryKind | null = null,
    division: string | null = null,
    ratedForMe = false,
  ) => ["contests", "directory", viewer, scope, page, kind, division, ratedForMe] as const,
  contest: (viewer: string, slug: string) => ["contests", "contest", viewer, slug] as const,
  /** `EX-DIV04`: the round page, with this reader's placement on it. */
  round: (viewer: string, slug: string) => ["contests", "round", viewer, slug] as const,
  problem: (viewer: string, slug: string, letter: string) =>
    ["contests", "problem", viewer, slug, letter] as const,
  mySubmissions: (slug: string, query: ContestSubmissionListQuery = {}) =>
    ["contests", "my-submissions", slug, query] as const,
  /**
   * The prefix, for invalidating every filter at once after a submit.
   *
   * `EX-PUB16` put the filter in the key, and TanStack matches an invalidation
   * by prefix: invalidating the full key would refresh the unfiltered list and
   * leave a contestant looking at a filtered one that still predates their
   * submission.
   */
  mySubmissionsAll: (slug: string) => ["contests", "my-submissions", slug] as const,
  /** `EX-SCB09`: My Submissions' Contests tab, every contest at once. */
  mySubmissionsEverywhere: (query: MyContestSubmissionsEverywhereQuery = {}) =>
    ["contests", "my-submissions-everywhere", query] as const,
  fieldSubmissions: (slug: string, query: ContestFieldSubmissionListQuery = {}) =>
    ["contests", "field-submissions", slug, query] as const,
  /** `EX-TST01`: the staff rehearsals, per letter filter, and one rehearsal. */
  rehearsals: (slug: string, letter = "") => ["contests", "rehearsals", slug, letter] as const,
  rehearsalsAll: (slug: string) => ["contests", "rehearsals", slug] as const,
  rehearsal: (slug: string, submissionId: string) =>
    ["contests", "rehearsal", slug, submissionId] as const,
  clarifications: (slug: string) => ["contests", "clarifications", slug] as const,
  announcements: (slug: string) => ["contests", "announcements", slug] as const,
  /** `EX-DAY06`: the certificates this reader may download on one contest. */
  certificates: (slug: string) => ["contests", "certificates", slug] as const,
  standings: (slug: string, view: string, page: number, institution = "", room = "", unofficial = false) =>
    ["contests", "standings", slug, view, page, institution, room, unofficial] as const,
  rating: (slug: string, page: number) => ["contests", "rating", slug, page] as const,
  /** `EX-VRT01`: the reader's virtual board, own list and dashboard card. */
  virtualStandings: (slug: string, page: number) => ["contests", "virtual-standings", slug, page] as const,
  virtualStandingsAll: (slug: string) => ["contests", "virtual-standings", slug] as const,
  myVirtualRounds: () => ["contests", "my-virtual-rounds"] as const,

  organizerList: (search: string, page: number) =>
    ["contest-platform", "list", search, page] as const,
  organizerContest: (id: string) => ["contest-platform", "contest", id] as const,
  organizerProblems: (id: string) => ["contest-platform", "problems", id] as const,
  /** `EX-PDF02`: who downloaded the problem set, per page; the prefix is what a download refreshes. */
  organizerProblemSetDownloadsAll: (id: string) => ["contest-platform", "problem-set-downloads", id] as const,
  organizerProblemSetDownloads: (id: string, page: number, limit: number) =>
    ["contest-platform", "problem-set-downloads", id, page, limit] as const,
  // `EX-SFX02`: keyed by the sorted ids, so reordering the draft asks nothing new.
  organizerProblemCheck: (id: string, ids: string) =>
    ["contest-platform", "problems", id, "check", ids] as const,
  /** `EX-STF01`: the pool per round, and the caller's own authored rounds. */
  organizerPool: (id: string) => ["contest-platform", "pool", id] as const,
  authoring: AUTHORING_KEY,
  organizerParticipants: (id: string, status: string, page: number) =>
    ["contest-platform", "participants", id, status, page] as const,
  organizerIssuedAccounts: (id: string) =>
    ["contest-platform", "participants", id, "issued-accounts"] as const,
  organizerTeams: (id: string) => ["contest-platform", "teams", id] as const,
  /** `EX-REG01`: the organizer's questions, and the answers of one roster page. */
  registrationQuestions: (id: string) => ["contest-platform", "registration-questions", id] as const,
  registrationAnswers: (id: string, userIds: readonly string[]) =>
    ["contest-platform", "registration-answers", id, userIds.join(",")] as const,
  myRegistrationAnswers: (slug: string) => ["contests", "registration-answers", slug] as const,
  organizerClarifications: (id: string, status: string, roomId = "") =>
    ["contest-platform", "clarifications", id, status, roomId] as const,
  /** `EX-ONS01`: the seat plan, per contest. */
  organizerVenue: (id: string) => ["contest-platform", "venue", id] as const,
  /** `EX-ONS02`: the contestant's own slips, the organizer's queue, one room's station. */
  printJobs: (slug: string) => ["contests", "print", slug] as const,
  organizerPrint: (id: string, roomId = "", status = "") =>
    ["contest-platform", "print", id, roomId, status] as const,
  organizerPrintStation: (id: string, roomId: string) =>
    ["contest-platform", "print-station", id, roomId] as const,
  /** `EX-ONS03`: the balloon board, with its filters in the key. */
  organizerBalloons: (id: string, roomId = "", status = "") =>
    ["contest-platform", "balloons", id, roomId, status] as const,
  organizerAnnouncements: (id: string) => ["contest-platform", "announcements", id] as const,
  organizerSubmissions: (id: string, query: OrganizerSubmissionsQuery) =>
    ["contest-platform", "submissions", id, query] as const,
  organizerSubmission: (id: string, submissionId: string) =>
    ["contest-platform", "submission", id, submissionId] as const,
  /**
   * `EX-PRC01`. Not keyed by contest id: a pack belongs to the caller, not to a
   * contest, and keying it per contest would cache the same list under every
   * console the organizer opens and then leave the stale copies behind after a
   * schedule spends one.
   */
  myEventPacks: ["contest-platform", "event-packs"] as const,
  /** `EX-PKG03`: the caller's unspent add-ons on a pack and setter credits. */
  myPackAddons: ["contest-platform", "pack-addons"] as const,
  setterAiCredits: (id: string) => ["contest-platform", "setter-ai-credits", id] as const,
  myOnsiteKits: ["contest-platform", "onsite-kits"] as const,
  /** `EX-DAY05`: the reveal's board, per contest. */
  organizerReveal: (id: string) => ["contest-platform", "reveal", id] as const,
  /** `EX-ANM01`: the solve-time report, per contest. */
  organizerTiming: (id: string) => ["contest-platform", "timing", id] as const,
  organizerReport: (id: string) => ["contest-platform", "report", id] as const,
  /** `EX-HAL01`: the hall's network guide, per contest. */
  organizerHallGuide: (id: string) => ["contest-platform", "hall-guide", id] as const,
  organizerVenueLock: (id: string) => ["contest-platform", "venue", id] as const,
  organizerVenueRefusals: (id: string) => ["contest-platform", "venue-refusals", id] as const,
  organizerVenueRoomCode: (id: string) => ["contest-platform", "venue-room-code", id] as const,
  /** `EX-OVL01`: the Broadcast section's links and switches. */
  organizerBroadcast: (id: string) => ["contest-platform", "broadcast", id] as const,
  organizerFeed: (id: string) => ["contest-platform", "feed", id] as const,
  /** `EX-SPN01`: the Sponsors and look section, and the sponsor sheet. */
  organizerBrand: (id: string) => ["contest-platform", "brand", id] as const,
  talentPass: (slug: string) => ["contests", "talent-pass", slug] as const,
  organizerSponsorSheet: (id: string) => ["contest-platform", "sponsor-sheet", id] as const,
  /** `EX-INS01`: a round's institutions, a season's page, and the console's seasons. */
  institutions: (slug: string) => ["contests", "institutions", slug] as const,
  publicSeason: (slug: string) => ["contest-seasons", slug] as const,
  mySeasons: () => ["contest-platform", "seasons"] as const,
  season: (id: string) => ["contest-platform", "season", id] as const,
  contestSeasons: (id: string) => ["contest-platform", "contest-seasons", id] as const,
  organizerSimilarity: (id: string) => ["contest-platform", "similarity", id] as const,
  organizerSimilarityPair: (id: string, problemId: string, left: string, right: string) =>
    ["contest-platform", "similarity", id, "pair", problemId, left, right] as const,
  organizerIntegrityReport: (id: string) => ["contest-platform", "integrity", id] as const,
  organizerIntegrityRecord: (id: string, participantId: string) =>
    ["contest-platform", "integrity", id, participantId] as const,
  /** `EX-INT07`: one entry's timeline, under the report's prefix so Recompute refreshes it. */
  organizerIntegrityTimeline: (id: string, participantId: string) =>
    ["contest-platform", "integrity", id, participantId, "timeline"] as const,
  /** `EX-EDO01`: the Editor only level's waiting list, under the report's prefix. */
  organizerHolds: (id: string) => ["contest-platform", "integrity", id, "holds"] as const,
  /** `EX-WSP01`: one entry's workspace stream for one problem. */
  organizerSnapshots: (id: string, participantId: string, problemId: string) =>
    ["contest-platform", "snapshots", id, participantId, problemId] as const,
};

/** Everything one contest's organizer console reads, refreshed after a write. */
function invalidateContest(qc: QueryClient, id: string): void {
  void qc.invalidateQueries({ queryKey: ["contest-platform"] });
  void qc.invalidateQueries({ queryKey: contestKeys.organizerContest(id) });
}

/* ── The contestant's side ────────────────────────────────────────────────── */

export function usePublicContests(
  viewerKey: string,
  scope: ContestDirectoryScope,
  page: number,
  kind: ContestDirectoryKind | null = null,
  enabled = true,
  /** `EX-DIV03`: a division chip (a preset id) and the signed-in reader's "Rated for me". */
  filters: { division?: string | null; ratedForMe?: boolean } = {},
) {
  const division = filters.division ?? null;
  const ratedForMe = filters.ratedForMe === true;
  return useQuery({
    queryKey: contestKeys.directory(viewerKey, scope, page, kind, division, ratedForMe),
    queryFn: () =>
      api.fetchPublicContests({
        scope,
        page,
        ...(kind ? { kind } : {}),
        ...(division ? { division } : {}),
        ...(ratedForMe ? { ratedForMe } : {}),
      }),
    placeholderData: keepPreviousData,
    enabled,
    retry: false,
  });
}

/**
 * `EX-DIV03`: the next round that rates this reader, for a contest page that
 * does not (an unofficial or a closed register box). The directory's own
 * "Rated for me" read, upcoming first, then running, never this contest.
 * `null` while loading, on a failure, or when there is none.
 */
export function useRatedForMeRound(slug: string, enabled: boolean) {
  const query = useQuery({
    queryKey: ["contests", "rated-for-me", slug] as const,
    queryFn: async () => {
      for (const scope of ["upcoming", "running"] as const) {
        const page = await api.fetchPublicContests({ scope, page: 1, limit: 5, ratedForMe: true });
        const found = page.items.find((item) => item.slug !== slug);
        if (found) return found;
      }
      return null;
    },
    enabled: enabled && !!slug,
    retry: false,
    staleTime: 5 * 60_000,
  });
  return query.data ?? null;
}

/**
 * `EX-EXT01`: how often an open page reads the contest again. A contest the
 * organizer extends moves its end on the server, and the countdown, the phase
 * and Submit read this query, so an open or just-ended contest is read every
 * minute and a page left open shows the new end within one. Exported for its
 * test.
 *
 * The start and the end are read again the second they pass (2026-10-25): the
 * kiosk on the projector said "Ended" and lost its clock at the start of
 * Weekly #1, because a scheduled contest was never read again and only a
 * refresh turned it live. A scheduled contest is read at its start (and every
 * ten minutes before, so a moved start shows), an open one at its end if that
 * comes before the minute. A read that lands a moment early, while the server
 * still says scheduled, tries again a few seconds later.
 */
export const OPEN_CONTEST_REFETCH_MS = 60_000;
export const SCHEDULED_CONTEST_REFETCH_MS = 10 * 60_000;
export const CONTEST_EDGE_RETRY_MS = 3_000;
function untilEdge(iso: string, now: number): number {
  return Math.max(CONTEST_EDGE_RETRY_MS, Date.parse(iso) - now + 1_000);
}
export function openContestRefetchInterval(
  contest: { isOpen?: boolean; phase?: string; startAt?: string | null; endAt?: string | null } | undefined,
  now = Date.now(),
): number | false {
  if (!contest) return false;
  if (contest.isOpen) {
    return contest.endAt ? Math.min(OPEN_CONTEST_REFETCH_MS, untilEdge(contest.endAt, now)) : OPEN_CONTEST_REFETCH_MS;
  }
  if (contest.phase === "scheduled" && contest.startAt) {
    return Math.min(SCHEDULED_CONTEST_REFETCH_MS, untilEdge(contest.startAt, now));
  }
  // An extension is accepted up to an hour after the end (the server's grace).
  const ended = contest.phase === "ended" && contest.endAt ? now - Date.parse(contest.endAt) : Number.POSITIVE_INFINITY;
  return ended <= 60 * 60_000 ? OPEN_CONTEST_REFETCH_MS : false;
}

export function usePublicContest(slug: string, viewerKey: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.contest(viewerKey, slug),
    queryFn: () => api.fetchPublicContest(slug),
    enabled: enabled && !!slug,
    retry: false,
    refetchInterval: (query) => openContestRefetchInterval(query.state.data),
  });
}

/**
 * `EX-DIV04`: the round page. While any division is inside the window it is
 * read again as the contest page is, so the countdown and the phase follow the
 * server's clock and an extension.
 */
export function usePublicRound(slug: string, viewerKey: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.round(viewerKey, slug),
    queryFn: () => api.fetchPublicRound(slug),
    enabled: enabled && !!slug,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.divisions.some((division) => division.phase === "running" || division.phase === "frozen")
        ? OPEN_CONTEST_REFETCH_MS
        : false,
  });
}

export function useRegisterForContest(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { passphrase?: string; acceptedRules?: boolean; answers?: RegistrationAnswerInput[] }) =>
      api.registerForContest(slug, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contests", "contest"] });
      // `EX-DIV04`: the round page says where the reader is now.
      void qc.invalidateQueries({ queryKey: ["contests", "round"] });
      // The directory card says "Registered" off its own read (2026-09-26).
      void qc.invalidateQueries({ queryKey: ["contests", "directory"] });
    },
  });
}

export function useContestProblem(
  slug: string,
  letter: string,
  viewerKey: string,
  enabled = true,
) {
  return useQuery({
    queryKey: contestKeys.problem(viewerKey, slug, letter),
    queryFn: () => api.fetchContestProblem(slug, letter),
    enabled: enabled && !!slug && !!letter,
    retry: false,
  });
}

/**
 * The contestant's own submissions, and the workspace's verdict poll.
 *
 * The verdict has no by-id read (`docs/29` §10; the one by-id read is the
 * program, `useMyContestSubmissionSource`), so the list *is* the poll - it
 * keeps refetching while any row is unfinished and stops when they have all
 * landed.
 */
export function useMyContestSubmissions(
  slug: string,
  enabled = true,
  /**
   * `EX-PUB16`: the filter, and it is part of the key.
   *
   * The default is an empty object, which hashes the same every time, so the
   * three surfaces that want "my submissions, unfiltered" - the workspace bar,
   * the problem list and the tab's first render - still share one request.
   */
  query: ContestSubmissionListQuery = {},
  /**
   * `EX-VRT01`: the reader holds a virtual try at this round, so their own
   * rows are the virtual ones (they never competed in it). Keyed apart.
   */
  virtual = false,
) {
  return useQuery({
    queryKey: virtual ? [...contestKeys.mySubmissions(slug, query), "virtual"] : contestKeys.mySubmissions(slug, query),
    queryFn: () => (virtual ? api.fetchMyVirtualSubmissions(slug, query) : api.fetchMyContestSubmissions(slug, query)),
    enabled: enabled && !!slug,
    retry: false,
    refetchInterval: (result) => {
      const data = result.state.data;
      if (!data) return false;
      return pendingPollInterval(result, data.items.filter(isContestSubmissionPending).length);
    },
  });
}

/**
 * The whole field's submissions, which the server serves only once the
 * scoreboard is open (`EX-PUB16`).
 *
 * No poll: the round is over by the time this can answer at all, so the only
 * thing that moves it is a rejudge, and a rejudge is somebody watching the
 * organizer console rather than this page.
 */
export function useContestFieldSubmissions(
  slug: string,
  query: ContestFieldSubmissionListQuery = {},
  enabled = true,
) {
  return useQuery({
    queryKey: contestKeys.fieldSubmissions(slug, query),
    queryFn: () => api.fetchContestFieldSubmissions(slug, query),
    enabled: enabled && !!slug,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/** `EX-SRC01`: one program from a published round, asked for when a row is opened. */
export function useContestFieldSubmissionSource(slug: string, submissionId: string | null) {
  return useQuery({
    queryKey: [...contestKeys.fieldSubmissions(slug), "source", submissionId ?? ""],
    queryFn: () => api.fetchContestFieldSubmissionSource(slug, submissionId!),
    enabled: !!slug && !!submissionId,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * `EX-SCB05`: one of the reader's own programs, asked for when the standings
 * popup opens an attempt on their own row. A program never changes once
 * submitted, so it stays fresh for as long as the field program does.
 */
/**
 * `EX-SCB09`: the caller's own contest submissions across every contest, for
 * My Submissions' Contests tab. The last page stays on screen while a filter's
 * page loads; it is the same reader's, so it never shows somebody else's rows.
 */
export function useMyContestSubmissionsEverywhere(query: MyContestSubmissionsEverywhereQuery = {}, enabled = true) {
  return useQuery({
    queryKey: contestKeys.mySubmissionsEverywhere(query),
    queryFn: () => api.fetchMyContestSubmissionsEverywhere(query),
    enabled,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

export function useMyContestSubmissionSource(slug: string, submissionId: string | null, virtual = false) {
  return useQuery({
    queryKey: [...contestKeys.mySubmissionsAll(slug), "source", submissionId ?? "", virtual ? "virtual" : "real"],
    queryFn: () =>
      virtual ? api.fetchMyVirtualSubmissionSource(slug, submissionId!) : api.fetchMyContestSubmissionSource(slug, submissionId!),
    enabled: !!slug && !!submissionId,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useSubmitToContest(slug: string, letter: string, virtual = false) {
  const qc = useQueryClient();
  return useMutation({
    // `EX-VRT01`: inside a running virtual try the virtual route, never the contest's.
    mutationFn: (input: ContestSubmitInput) =>
      virtual ? api.submitVirtual(slug, letter, input) : api.submitToContest(slug, letter, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contestKeys.mySubmissionsAll(slug) });
      if (virtual) void qc.invalidateQueries({ queryKey: contestKeys.virtualStandingsAll(slug) });
    },
  });
}

/** `EX-EDR02`: a run is feedback, so nothing is invalidated after it. */
export function useRunContestCode(slug: string, letter: string, virtual = false) {
  return useMutation({
    mutationFn: (input: RunSampleInput) =>
      virtual ? api.runVirtualCode(slug, letter, input) : api.runContestCode(slug, letter, input),
  });
}

/* ── A virtual try (`EX-VRT01`, `docs/29` §6.22) ───────────────────────────── */

/** Start or finish; either way the page, the board and the dashboard read again. */
export function useVirtualRoundAction(slug: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["contests", "contest"] });
    void qc.invalidateQueries({ queryKey: ["contests", "problem"] });
    void qc.invalidateQueries({ queryKey: contestKeys.virtualStandingsAll(slug) });
    void qc.invalidateQueries({ queryKey: contestKeys.mySubmissionsAll(slug) });
    void qc.invalidateQueries({ queryKey: contestKeys.myVirtualRounds() });
    // The practice page's editorial and hints answer differently once a try starts or ends.
    void qc.invalidateQueries({ queryKey: ["judge", "editorial"] });
  };
  return {
    start: useMutation({ mutationFn: () => api.startVirtualRound(slug), onSuccess: refresh }),
    finish: useMutation({ mutationFn: () => api.finishVirtualRound(slug), onSuccess: refresh }),
  };
}

/**
 * The real board at the try's minute. A minute is the board's unit, so it
 * reads again every half minute while the try runs, and never once it is over.
 */
export function useVirtualStandings(slug: string, page: number, enabled: boolean, running: boolean) {
  return useQuery({
    queryKey: contestKeys.virtualStandings(slug, page),
    queryFn: () => api.fetchVirtualStandings(slug, page),
    enabled: enabled && !!slug,
    placeholderData: keepPreviousData,
    retry: false,
    refetchInterval: running ? 30_000 : false,
  });
}

/** The dashboard's card. */
export function useMyVirtualRounds(enabled = true) {
  return useQuery({
    queryKey: contestKeys.myVirtualRounds(),
    queryFn: () => api.fetchMyVirtualRounds(),
    enabled,
    retry: false,
  });
}

/**
 * `EX-TST01`: the round's rehearsals, for its staff. Polls while any row is
 * still waiting for a verdict, the way the contestant's own list does.
 */
export function useContestRehearsals(slug: string, letter = "", enabled = true) {
  return useQuery({
    queryKey: contestKeys.rehearsals(slug, letter),
    queryFn: () => api.fetchContestRehearsals(slug, letter ? { letter } : {}),
    enabled: enabled && !!slug,
    retry: false,
    refetchInterval: (result) => {
      const data = result.state.data;
      if (!data) return false;
      return pendingPollInterval(result, data.items.filter(isContestSubmissionPending).length);
    },
  });
}

/** `EX-TST01`: one rehearsal's per-test detail, polled until it lands. */
export function useContestRehearsal(slug: string, submissionId: string | null) {
  return useQuery({
    queryKey: contestKeys.rehearsal(slug, submissionId ?? ""),
    queryFn: () => api.fetchContestRehearsal(slug, submissionId ?? ""),
    enabled: !!slug && !!submissionId,
    retry: false,
    refetchInterval: (result) => {
      const data = result.state.data;
      if (!data) return false;
      return pendingPollInterval(result, isContestSubmissionPending(data) ? 1 : 0);
    },
  });
}

export function useSubmitRehearsal(slug: string, letter: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContestSubmitInput) => api.submitRehearsal(slug, letter, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contestKeys.rehearsalsAll(slug) });
    },
  });
}

/** `EX-ANN02`: every 30 seconds while the round is live, like the announcements. */
export function useContestClarifications(slug: string, enabled = true, contest?: AnnouncementWindow) {
  const live = announcementsLive(contest);
  return useQuery({
    queryKey: contestKeys.clarifications(slug),
    queryFn: () => api.fetchContestClarifications(slug),
    enabled: enabled && !!slug,
    retry: false,
    refetchInterval: live ? LIVE_ANNOUNCEMENTS_REFETCH_MS : 60_000,
    refetchIntervalInBackground: live,
  });
}

export function useAskClarification(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { letter?: string; question: string }) =>
      api.askContestClarification(slug, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contestKeys.clarifications(slug) });
    },
  });
}

/**
 * Readable by anyone who can see the contest page, registered or not.
 * `EX-ANN01`: every 30 seconds while the round is live, in a background tab
 * too (the tab title counts what is new), and every minute otherwise.
 */
export const LIVE_ANNOUNCEMENTS_REFETCH_MS = 30_000;
export function useContestAnnouncements(slug: string, enabled = true, contest?: AnnouncementWindow) {
  const live = announcementsLive(contest);
  return useQuery({
    queryKey: contestKeys.announcements(slug),
    queryFn: () => api.fetchContestAnnouncements(slug),
    enabled: enabled && !!slug,
    retry: false,
    refetchInterval: live ? LIVE_ANNOUNCEMENTS_REFETCH_MS : 60_000,
    refetchIntervalInBackground: live,
  });
}

/**
 * `EX-ANN01`: the announcements this browser has not acknowledged, newest
 * first, for the alert on every contest page. Empty until the stored marker is
 * read on the client, so the server render and the first client render agree.
 */
export function useAnnouncementInbox(slug: string, contest: AnnouncementWindow | undefined, enabled = true) {
  const query = useContestAnnouncements(slug, enabled, contest);
  const [seenAt, remember] = useSeenMarker(announcementsSeenKey(slug));
  const items = query.data?.items;
  const alerting = seenAt !== undefined && announcementsAlerting(contest);
  const unseen = useMemo(
    () => (alerting && items ? unseenAnnouncements(items, seenAt ?? null) : []),
    [alerting, items, seenAt],
  );
  const markSeen = useCallback(() => {
    const newest = newestAnnouncementStamp(items ?? []);
    if (newest !== null) remember(newest);
  }, [items, remember]);
  return { unseen, markSeen, total: query.data?.total };
}

/**
 * `EX-ANN02`: the answered clarifications this browser has not acknowledged,
 * the reply to the viewer's own question and the broadcasts, newest first.
 * Only a competing viewer is answered by the route (`docs/29` §6.2), and a
 * moderator has the queue, so nobody else polls.
 */
export function useClarificationInbox(
  slug: string,
  contest: (AnnouncementWindow & { viewer?: { isCompeting?: boolean } }) | undefined,
  enabled = true,
) {
  const competing = contest?.viewer?.isCompeting === true;
  const query = useContestClarifications(slug, enabled && competing, contest);
  const [seenAt, remember] = useSeenMarker(clarificationsSeenKey(slug));
  const items = query.data?.items;
  const alerting = competing && seenAt !== undefined && announcementsAlerting(contest);
  const unseen = useMemo(
    () => (alerting && items ? unseenClarificationAnswers(items, seenAt ?? null) : []),
    [alerting, items, seenAt],
  );
  const markSeen = useCallback(() => {
    const newest = newestClarificationAnswerStamp(items ?? []);
    if (newest !== null) remember(newest);
  }, [items, remember]);
  return { unseen, markSeen };
}

/**
 * One acknowledged instant in `localStorage`, read on the client only (so the
 * server render and the first client render agree: `undefined` until then),
 * and followed across tabs through the storage event.
 */
function useSeenMarker(key: string) {
  const [seenAt, setSeenAt] = useState<number | null | undefined>(undefined);
  useEffect(() => {
    setSeenAt(readSeenMarker(key));
    const onStorage = (event: StorageEvent) => {
      if (event.key === key) setSeenAt(readSeenMarker(key));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key]);
  const remember = useCallback(
    (at: number) => {
      rememberSeenMarker(key, at);
      setSeenAt(at);
    },
    [key],
  );
  return [seenAt, remember] as const;
}

export function useContestStandings(
  slug: string,
  params: {
    view: StandingsView;
    page: number;
    institution?: string;
    room?: string;
    limit?: number;
    unofficial?: "show";
  },
  enabled = true,
  options: { refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: contestKeys.standings(
      slug,
      params.view,
      params.page,
      params.institution ?? "",
      params.room ?? "",
      params.unofficial === "show",
    ),
    queryFn: () => api.fetchContestStandings(slug, params),
    enabled: enabled && !!slug,
    placeholderData: keepPreviousData,
    retry: false,
    // The scoreboard is a fold over live rows, so a running contest's table is
    // stale the moment a verdict lands. `EX-SCB01`: the kiosk asks for a
    // tighter cadence, and the server's five-second cache bounds what it costs.
    refetchInterval: options.refetchInterval ?? 30_000,
  });
}

/* ── The organizer's side ─────────────────────────────────────────────────── */

export function useOrganizerContests(search: string, page: number) {
  return useQuery({
    queryKey: contestKeys.organizerList(search, page),
    queryFn: () => api.fetchOrganizerContests({ search: search || undefined, page }),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useOrganizerContest(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerContest(id),
    queryFn: () => api.fetchOrganizerContest(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

export function useCreateContest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContestInput) => api.createContest(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contest-platform", "list"] });
    },
  });
}

/** `EX-SRC01`: the Submissions section's publish switch. */
export function useSetSubmissionsPublished(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (published: boolean) => api.setSubmissionsPublished(id, published),
    onSuccess: () => invalidateContest(qc, id),
  });
}

export function useUpdateContest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateContestInput) => api.updateContest(id, input),
    onSuccess: () => invalidateContest(qc, id),
  });
}

/** `EX-EXT01`: the owner extends a started contest. */
export function useExtendContest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ExtendContestInput) => api.extendContest(id, input),
    onSuccess: () => invalidateContest(qc, id),
  });
}

/**
 * The owner's ask, and its withdrawal (`EX-RRQ01`, `docs/29` §7.1).
 *
 * Same invalidation as every other organizer write: the answer and the state
 * both live on the contest's own `ratedRequest`, so refetching the contest is
 * refetching the card.
 *
 * There is no `usePromote` here and there never will be: promotion is an
 * administrator act on the operator table (§7.1), and this console never had a
 * route that could reach it.
 */
export function useRequestContestRated(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => api.requestContestRated(id, reason),
    onSuccess: () => invalidateContest(qc, id),
  });
}

export function useWithdrawContestRatedRequest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.withdrawContestRatedRequest(id),
    onSuccess: () => invalidateContest(qc, id),
  });
}

/**
 * `EX-DAY03`: the owner's rehearsal booking. Named `...RehearsalBooking` so
 * nobody mistakes it for `EX-TST01`'s staff rehearsal runs, which are the
 * session the booking is for.
 */
export function useBookRehearsal(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (startAt: string) => api.bookContestRehearsal(id, startAt),
    onSuccess: () => invalidateContest(qc, id),
  });
}

export function useCancelRehearsalBooking(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.cancelContestRehearsal(id),
    onSuccess: () => invalidateContest(qc, id),
  });
}

/** `EX-DAY06`: the owner saves the certificates' settings. */
export function useSaveContestCertificates(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContestCertificateSettingsInput) => api.saveContestCertificates(id, input),
    onSuccess: () => invalidateContest(qc, id),
  });
}

/**
 * `EX-DAY06`: the certificates this reader may download. Read only when the
 * card is on screen; a download that issues one refreshes the list, so the
 * verify link appears beside it.
 */
export function useContestCertificates(slug: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.certificates(slug),
    queryFn: () => api.fetchContestCertificates(slug),
    enabled: enabled && !!slug,
    retry: false,
  });
}

/** `EX-CRT01`: email the certificates, or one person's again; the list refreshes. */
export function useEmailContestCertificates(id: string, slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { recipientId?: string; resend?: boolean }) => api.emailContestCertificates(id, body),
    onSettled: () => qc.invalidateQueries({ queryKey: contestKeys.certificates(slug) }),
  });
}

/** `EX-DAY04`: create the pack contest's warm-up; both contests' reads refresh. */
export function useCreateWarmUp(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { startAt: string; endAt: string }) => api.createContestWarmUp(id, input),
    onSuccess: () => invalidateContest(qc, id),
  });
}

export function useDeleteContest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteContest(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contest-platform", "list"] });
    },
  });
}

export type LifecycleAction = "schedule" | "unschedule" | "unfreeze";

/**
 * `EX-PRC01`: schedule can now carry an Event Pack, so the mutation takes an
 * action **or** an action with options rather than a bare string. The bare
 * string keeps working for the two transitions that have nothing to choose.
 */
export type LifecycleInput =
  | LifecycleAction
  /**
   * `EX-PKG02` widened this by one optional flag. `useOnsiteKit` rides beside
   * `eventPackId` (ADR-0060 decision 10), except where the owner's plan carries
   * a sized hall (`EX-GAT03`, ADR-0089: `capabilityPreview.hallPlanCap > 0`),
   * where it may be sent alone; the server refuses every other pairing with a 400.
   */
  | { action: "schedule"; eventPackId?: string; useOnsiteKit?: boolean }
  /**
   * `EX-ORG02`, wired on the web console with the rail (2026-09-18): the
   * institution's allowance, which the server refuses beside a pack
   * (`conflicting_funding`), so it is its own shape rather than a third flag.
   */
  | { action: "schedule"; useOrgAllowance: true };

export function useContestLifecycle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LifecycleInput) => {
      const action = typeof input === "string" ? input : input.action;
      if (action === "schedule") {
        if (typeof input !== "string" && "useOrgAllowance" in input) {
          return api.scheduleContest(id, { useOrgAllowance: true });
        }
        return api.scheduleContest(
          id,
          typeof input === "string" || !input.eventPackId
            ? {}
            : input.useOnsiteKit
              ? { eventPackId: input.eventPackId, useOnsiteKit: true }
              : { eventPackId: input.eventPackId },
        );
      }
      return action === "unschedule" ? api.unscheduleContest(id) : api.unfreezeContest(id);
    },
    onSuccess: () => {
      invalidateContest(qc, id);
      // Scheduling spends a pack and unscheduling gives it back, so the pack
      // list is stale the moment either lands. Without this the organizer sees
      // a pack they can no longer spend, and is refused when they try.
      void qc.invalidateQueries({ queryKey: contestKeys.myEventPacks });
      void qc.invalidateQueries({ queryKey: contestKeys.myOnsiteKits });
    },
  });
}

/**
 * The caller's unconsumed Event Packs — read by the schedule control to decide
 * whether to offer the choice at all.
 *
 * `enabled` because it is only ever asked on a draft an owner can schedule: a
 * moderator has no packs to spend here, and a contest that is already running
 * has nothing left to choose.
 */
/** `EX-PKG03`: the caller's unspent add-ons and setter credits, read by the Overview's add-ons card. */
export function useMyPackAddons(enabled = true) {
  return useQuery({
    queryKey: contestKeys.myPackAddons,
    queryFn: api.fetchMyPackAddons,
    enabled,
    staleTime: 60 * 1000,
  });
}

/** `EX-PKG03`: spend an add-on on this contest; the contest and the holdings refresh. */
export function useSpendPackAddon(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (item: ContestStampAddonItem) => api.spendPackAddon(id, item),
    onSuccess: () => {
      invalidateContest(qc, id);
      void qc.invalidateQueries({ queryKey: contestKeys.myPackAddons });
    },
  });
}

/** `EX-PKG03`: the setters' AI credits on this contest, the owner's to give. */
export function useSetterAiCredits(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.setterAiCredits(id),
    queryFn: () => api.fetchSetterAiCredits(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

export function useGiveSetterAiCredits(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; amount: number }) => api.giveSetterAiCredits(id, input),
    onSuccess: (data) => {
      qc.setQueryData(contestKeys.setterAiCredits(id), data);
      void qc.invalidateQueries({ queryKey: contestKeys.myPackAddons });
    },
  });
}

export function useMyEventPacks(enabled = true) {
  return useQuery({
    queryKey: contestKeys.myEventPacks,
    queryFn: api.fetchMyEventPacks,
    enabled,
    staleTime: 60 * 1000,
  });
}

/**
 * The caller's unconsumed Onsite Kits - `EX-PKG02`, the `useMyEventPacks`
 * sibling, asked under the same condition and for the same reason.
 */
export function useMyOnsiteKits(enabled = true) {
  return useQuery({
    queryKey: contestKeys.myOnsiteKits,
    queryFn: api.fetchMyOnsiteKits,
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useContestModerators(id: string) {
  const qc = useQueryClient();
  // `onSettled`, not `onSuccess`, on every write below that names somebody already on
  // the list: a refusal such as "no longer on this list" or "already answered" means
  // the screen is stale, and the panel tells the reader the list is being refreshed.
  const remove = useMutation({
    mutationFn: (userId: string) => api.removeContestModerator(id, userId),
    onSettled: () => invalidateContest(qc, id),
  });
  // `EX-COL07`: an offer the moderator accepts; the owner is unchanged until then.
  const transfer = useMutation({
    mutationFn: (userId: string) => api.transferContestOwnership(id, userId),
    onSettled: () => invalidateContest(qc, id),
  });
  return { remove, transfer };
}

/* ── `EX-STF01`: staff and the pool (`docs/29` §4.1, §4.2, §10) ──────────── */

/** What the staff box sends: a list, the person, and `lead` for an author. */
export type InviteContestStaffInput = {
  role: ContestStaffListRole;
  person: ContestStaffInput;
  lead?: boolean;
};

/**
 * `EX-COL07`: every add to a contest's staff is an invitation the person
 * accepts, whichever of the four lists it is for. The detail is invalidated,
 * not patched: the new row is a waiting invitation in `pendingInvitations`,
 * and the server's answer for it is the one to show. A refused invite changes
 * nothing, so only a success reads the contest again.
 */
export function useInviteContestStaff(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ role, person, lead }: InviteContestStaffInput) =>
      api.inviteContestStaff(id, role, person, lead),
    onSuccess: () => invalidateContest(qc, id),
  });
}

/** `EX-COL07`: withdraw a waiting staff invitation or the ownership offer. */
export function useCancelContestInvitation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => api.cancelContestInvitation(id, invitationId),
    onSettled: () => invalidateContest(qc, id),
  });
}

/** The staff lists' other writes, the owner's `own`; each returns the hydrated contest. */
export function useContestStaff(id: string) {
  const qc = useQueryClient();
  const done = () => invalidateContest(qc, id);
  const setLead = useMutation({
    mutationFn: ({ userId, lead }: { userId: string; lead: boolean }) =>
      api.setContestAuthorLead(id, userId, lead),
    onSettled: done,
  });
  const removeAuthor = useMutation({
    mutationFn: (userId: string) => api.removeContestAuthor(id, userId),
    onSettled: done,
  });
  const removeTester = useMutation({
    mutationFn: (userId: string) => api.removeContestTester(id, userId),
    onSettled: done,
  });
  // `EX-ONS03`: the third list, the runners.
  const removeVolunteer = useMutation({
    mutationFn: (userId: string) => api.removeContestVolunteer(id, userId),
    onSettled: done,
  });
  return { setLead, removeAuthor, removeTester, removeVolunteer };
}

export function useContestPool(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerPool(id),
    queryFn: () => api.fetchContestPool(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

/**
 * Offer and withdraw. Both refresh the authoring list as well as the round's
 * own reads, because "Rounds I author" carries the caller's offers.
 */
export function usePoolActions(id: string) {
  const qc = useQueryClient();
  const done = (data: Awaited<ReturnType<typeof api.fetchContestPool>>) => {
    qc.setQueryData(contestKeys.organizerPool(id), data);
    invalidateContest(qc, id);
  };
  const offer = useMutation({
    mutationFn: (problemId: string) => api.offerProblemToPool(id, problemId),
    onSuccess: done,
  });
  const withdraw = useMutation({
    mutationFn: (problemId: string) => api.withdrawProblemFromPool(id, problemId),
    onSuccess: done,
  });
  return { offer, withdraw };
}

/** The rounds the signed-in user is an author of (`GET /v1/contest-platform/authoring`). */
export function useAuthoring(enabled = true) {
  return useQuery({ ...authoringQuery(), enabled });
}

/**
 * The "Offer to round" control on My problems: an offer into any round, keyed
 * by that round, refreshing the authoring list the control reads.
 */
export function useOfferToRound() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contestId, problemId }: { contestId: string; problemId: string }) =>
      api.offerProblemToPool(contestId, problemId),
    onSuccess: (_data, { contestId }) => invalidateContest(qc, contestId),
  });
}

/** The offer's undo, from the same control, while the problem is unmounted. */
export function useWithdrawFromRound() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contestId, problemId }: { contestId: string; problemId: string }) =>
      api.withdrawProblemFromPool(contestId, problemId),
    onSuccess: (_data, { contestId }) => invalidateContest(qc, contestId),
  });
}

export function useOrganizerProblemSet(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerProblems(id),
    queryFn: () => api.fetchOrganizerProblemSet(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

/**
 * `EX-SFX02`, ADR-0107: what the next save would say about these problems,
 * before Save. Off when there is nothing to ask; the previous answer stays on
 * screen while a new set of ids is asked.
 */
export function useContestProblemCheck(id: string, problemIds: readonly string[], enabled = true) {
  const ids = [...new Set(problemIds)].sort().slice(0, CONTEST_PROBLEM_CHECK_MAX);
  return useQuery({
    queryKey: contestKeys.organizerProblemCheck(id, ids.join(",")),
    queryFn: () => api.fetchContestProblemCheck(id, ids),
    enabled: enabled && !!id && ids.length > 0,
    placeholderData: (prev) => prev,
    staleTime: 15_000,
    retry: false,
  });
}

/** `EX-PDF02`: who downloaded the problem set (`moderate`); off for everyone else. */
export function useProblemSetDownloads(id: string, page: number, limit: number, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerProblemSetDownloads(id, page, limit),
    queryFn: () => api.fetchProblemSetDownloads(id, { page, limit }),
    enabled: enabled && !!id,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Where a problem set PDF stands (the shared run, `features/problem-set-pdf`). */
export type ContestPdfRun = PdfRun;

export type ContestPdfRequest = {
  path: string;
  fallbackFilename: string;
  action: PdfAction;
  /** The line the print tab shows while the PDF is made. */
  waiting: string;
};

/**
 * `EX-PDF02`: one PDF at a time from one control, a contest route's `GET`
 * through the shared run (`EX-PDF03` lifted it for Author Studio). A
 * delivered PDF refreshes the downloads list, whose newest row is it.
 */
export function useContestPdf(contestId: string) {
  const qc = useQueryClient();
  const refresh = useCallback(
    () => void qc.invalidateQueries({ queryKey: contestKeys.organizerProblemSetDownloadsAll(contestId) }),
    [contestId, qc],
  );
  const pdf = usePdfRun(refresh);
  const { start: startRun } = pdf;

  const start = useCallback(
    (request: ContestPdfRequest) =>
      startRun({
        load: () => api.fetchContestPdf(request.path, request.fallbackFilename),
        action: request.action,
        waiting: request.waiting,
      }),
    [startRun],
  );

  return { run: pdf.run, start, reset: pdf.reset };
}

export function useSetContestProblems(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      problemIds: string[];
      problemMaxScores?: { problemId: string; maxScore: number }[];
      problemBalloonColors?: { problemId: string; hex: string; name: string }[];
    }) => api.setContestProblems(id, input.problemIds, input.problemMaxScores, input.problemBalloonColors),
    onSuccess: () => invalidateContest(qc, id),
  });
}

/* ── `EX-REG01`: registration questions ─────────────────────────────────── */

/** The contestant's own answers, read when they open the form to change them. */
export function useMyRegistrationAnswers(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: contestKeys.myRegistrationAnswers(slug),
    queryFn: () => api.fetchMyRegistrationAnswers(slug),
    enabled: enabled && !!slug,
    retry: false,
  });
}

export function useUpdateMyRegistrationAnswers(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (answers: RegistrationAnswerInput[]) => api.updateMyRegistrationAnswers(slug, answers),
    onSuccess: (data) => {
      qc.setQueryData(contestKeys.myRegistrationAnswers(slug), data);
      void qc.invalidateQueries({ queryKey: ["contests", "contest"] });
    },
  });
}

export function useUploadRegistrationFile(slug: string) {
  return useMutation({
    mutationFn: (input: { questionId: string; file: File }) =>
      api.uploadRegistrationFile(slug, input.questionId, input.file),
  });
}

export function useRegistrationQuestions(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.registrationQuestions(id),
    queryFn: () => api.fetchRegistrationQuestions(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

export function useSaveRegistrationQuestions(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (questions: RegistrationQuestionInput[]) => api.saveRegistrationQuestions(id, questions),
    onSuccess: (data) => {
      qc.setQueryData(contestKeys.registrationQuestions(id), data);
      void qc.invalidateQueries({ queryKey: ["contest-platform", "registration-answers", id] });
    },
  });
}

/** The answers of the people one roster page shows; nothing is read for a contest that asks nothing. */
export function useRegistrationAnswers(id: string, userIds: readonly string[], enabled: boolean) {
  return useQuery({
    queryKey: contestKeys.registrationAnswers(id, userIds),
    queryFn: () => api.fetchRegistrationAnswers(id, userIds),
    enabled: enabled && !!id && userIds.length > 0,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useOpenRegistrationFile(id: string) {
  return useMutation({ mutationFn: (fileId: string) => api.openRegistrationFile(id, fileId) });
}

export function useOrganizerParticipants(
  id: string,
  params: { status: string; page: number },
  enabled = true,
) {
  return useQuery({
    queryKey: contestKeys.organizerParticipants(id, params.status, params.page),
    queryFn: () =>
      api.fetchOrganizerParticipants(id, {
        page: params.page,
        ...(params.status ? { status: params.status } : {}),
      }),
    enabled: enabled && !!id,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/**
 * Every issued contest account (`EX-TMP01`), across every page of the
 * participant list. The Contest accounts section read page one only, fifty
 * rows, so on a round with more than fifty people an account further down had
 * no row and no way to regenerate its password (2026-09-24).
 */
export function useIssuedContestAccounts(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerIssuedAccounts(id),
    queryFn: async () => {
      const limit = 200;
      const first = await api.fetchOrganizerParticipants(id, { page: 1, limit });
      const rows = [...first.items];
      for (let page = 2; page <= first.totalPages; page += 1) {
        const next = await api.fetchOrganizerParticipants(id, { page, limit });
        rows.push(...next.items);
      }
      return rows.filter((row) => row.accountKind === "contest_temp");
    },
    enabled: enabled && !!id,
    retry: false,
  });
}

export function useParticipantActions(id: string) {
  const qc = useQueryClient();
  const move = useMutation({
    mutationFn: (input: { userId: string; action: api.ParticipantAction; reason?: string }) =>
      api.moveContestParticipant(id, input.userId, input.action, input.reason),
    onSuccess: () => invalidateContest(qc, id),
  });
  const invite = useMutation({
    mutationFn: (person: ContestPersonInput) => api.inviteContestParticipant(id, person),
    onSuccess: () => invalidateContest(qc, id),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.removeContestParticipant(id, userId),
    onSuccess: () => invalidateContest(qc, id),
  });
  return { move, invite, remove };
}

export function useOrganizerTeams(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerTeams(id),
    queryFn: () => api.fetchOrganizerTeams(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

/**
 * `EX-ONS01`: the seat plan. Read only when the console has the Onsite Kit,
 * because the route answers 403 without it and a refusal is not a plan.
 */
export function useContestVenue(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerVenue(id),
    queryFn: () => api.fetchContestVenue(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

export function useVenueActions(id: string) {
  const qc = useQueryClient();
  const settled = {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contestKeys.organizerVenue(id) });
      // The queue's seat column and the team panel's seat both read the plan.
      void qc.invalidateQueries({ queryKey: ["contest-platform", "clarifications", id] });
      void qc.invalidateQueries({ queryKey: contestKeys.organizerTeams(id) });
    },
  };
  return {
    putVenue: useMutation({ mutationFn: (input: { name: string }) => api.putContestVenue(id, input), ...settled }),
    createRoom: useMutation({
      mutationFn: (input: CreateContestRoomInput) => api.createContestRoom(id, input),
      ...settled,
    }),
    renameRoom: useMutation({
      mutationFn: (input: { roomId: string; name: string }) =>
        api.renameContestRoom(id, input.roomId, { name: input.name }),
      ...settled,
    }),
    deleteRoom: useMutation({ mutationFn: (roomId: string) => api.deleteContestRoom(id, roomId), ...settled }),
    addSeats: useMutation({
      mutationFn: (input: { roomId: string; labels: string[] }) =>
        api.addContestRoomSeats(id, input.roomId, { labels: input.labels }),
      ...settled,
    }),
    assign: useMutation({
      mutationFn: (input: { seatId: string } & AssignContestSeatInput) => {
        const { seatId, ...entry } = input;
        return api.assignContestSeat(id, seatId, entry);
      },
      ...settled,
    }),
    release: useMutation({ mutationFn: (seatId: string) => api.releaseContestSeat(id, seatId), ...settled }),
    setStatus: useMutation({
      mutationFn: (input: { seatId: string; status: "free" | "blocked" }) =>
        api.setContestSeatStatus(id, input.seatId, input.status),
      ...settled,
    }),
    spread: useMutation({ mutationFn: (input: { reset?: boolean }) => api.spreadContestSeats(id, input), ...settled }),
  };
}

export function useTeamActions(id: string) {
  const qc = useQueryClient();
  const settled = { onSuccess: () => invalidateContest(qc, id) };
  return {
    create: useMutation({
      mutationFn: (input: { name: string; memberIds?: string[] }) =>
        api.createContestTeam(id, input),
      ...settled,
    }),
    rename: useMutation({
      mutationFn: (input: { teamId: string; name: string }) =>
        api.updateContestTeam(id, input.teamId, { name: input.name }),
      ...settled,
    }),
    /** `EX-SCB01`: the institution, the logo, the country and the roster. */
    update: useMutation({
      mutationFn: (input: { teamId: string } & UpdateContestTeamInput) => {
        const { teamId, ...patch } = input;
        return api.updateContestTeam(id, teamId, patch);
      },
      ...settled,
    }),
    remove: useMutation({
      mutationFn: (teamId: string) => api.deleteContestTeam(id, teamId),
      ...settled,
    }),
    addMember: useMutation({
      mutationFn: (input: { teamId: string; person: ContestPersonInput }) =>
        api.addContestTeamMember(id, input.teamId, input.person),
      ...settled,
    }),
    removeMember: useMutation({
      mutationFn: (input: { teamId: string; userId: string }) =>
        api.removeContestTeamMember(id, input.teamId, input.userId),
      ...settled,
    }),
    move: useMutation({
      mutationFn: (input: {
        teamId: string;
        action: "disqualify" | "reinstate";
        reason?: string;
      }) => api.moveContestTeam(id, input.teamId, input.action, input.reason),
      ...settled,
    }),
  };
}

export function useClarificationQueue(id: string, status: string, enabled = true, roomId = "", limit?: number) {
  return useQuery({
    queryKey: [...contestKeys.organizerClarifications(id, status, roomId), limit ?? 0] as const,
    queryFn: () =>
      api.fetchClarificationQueue(id, {
        ...(status ? { status } : {}),
        ...(roomId ? { roomId } : {}),
        // `EX-CLA01`: the Waiting view reads every waiting question to group them.
        ...(limit ? { limit } : {}),
      }),
    enabled: enabled && !!id,
    retry: false,
    // The queue is worked through by a human while the contest runs.
    refetchInterval: 30_000,
  });
}

/* ── The Clarification Assistant (`EX-CLA01`) ─────────────────────────────── */

function invalidateQueue(qc: QueryClient, id: string): void {
  void qc.invalidateQueries({ queryKey: ["contest-platform", "clarifications", id] });
}

export function useGroupClarifications(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idempotencyKey: string) => api.groupClarifications(id, idempotencyKey),
    onSettled: () => invalidateQueue(qc, id),
  });
}

export function useDraftClarificationAnswer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { clarificationIds: string[]; idempotencyKey: string }) =>
      api.draftClarificationAnswer(id, input),
    // The included uses left move with every press.
    onSettled: () => invalidateQueue(qc, id),
  });
}

export function useSplitClarification(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clarificationId: string) => api.splitClarification(id, clarificationId),
    onSuccess: () => invalidateQueue(qc, id),
  });
}

export function useAnswerClarificationGroup(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      clarificationIds: string[];
      answer: string;
      visibility: "asker" | "public";
      aiAssisted: boolean;
      onlyWaiting: boolean;
    }) => api.answerClarificationGroup(id, input),
    onSuccess: () => invalidateContest(qc, id),
  });
}

export function useOrganizerAnnouncements(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerAnnouncements(id),
    queryFn: () => api.fetchOrganizerAnnouncements(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

export function useAnnouncementActions(id: string) {
  const qc = useQueryClient();
  const settled = { onSuccess: () => invalidateContest(qc, id) };
  return {
    post: useMutation({
      mutationFn: (input: { title?: string; body: string }) =>
        api.postContestAnnouncement(id, input),
      ...settled,
    }),
    update: useMutation({
      mutationFn: (input: { announcementId: string; title?: string; body?: string }) =>
        api.updateContestAnnouncement(id, input.announcementId, {
          ...(input.title !== undefined ? { title: input.title } : {}),
          ...(input.body !== undefined ? { body: input.body } : {}),
        }),
      ...settled,
    }),
    remove: useMutation({
      mutationFn: (announcementId: string) => api.deleteContestAnnouncement(id, announcementId),
      ...settled,
    }),
  };
}

export function useOrganizerSubmissions(
  id: string,
  query: OrganizerSubmissionsQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: contestKeys.organizerSubmissions(id, query),
    queryFn: () => api.fetchOrganizerSubmissions(id, query),
    enabled: enabled && !!id,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** One submission with its source - fetched only when a moderator opens it. */
export function useOrganizerSubmission(id: string, submissionId: string | null) {
  return useQuery({
    queryKey: contestKeys.organizerSubmission(id, submissionId ?? "none"),
    queryFn: () => api.fetchOrganizerSubmission(id, submissionId as string),
    enabled: !!id && !!submissionId,
    retry: false,
  });
}

export function useRejudge(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RejudgeInput) => api.rejudgeContestSubmissions(id, input),
    onSuccess: () => invalidateContest(qc, id),
  });
}

/* ── Rating (`EX-PUB07`) ──────────────────────────────────────────────────── */

/**
 * One contest's ledger. Not keyed by viewer: the ledger is the same table for
 * everybody who can read the contest, and `loadVisibleContest` has already
 * refused anyone who cannot.
 */
export function useContestRating(slug: string, page = 1, enabled = true) {
  return useQuery({
    queryKey: contestKeys.rating(slug, page),
    queryFn: () => api.fetchContestRating(slug, { page }),
    enabled: enabled && !!slug,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/**
 * The operator's three writes.
 *
 * Every one of them changes the scoreboard a contestant is looking at, so they
 * invalidate the contestant-side keys as well as the console's own - a console
 * that showed "applied" beside a standings table with no deltas would be two
 * views of one contest disagreeing.
 */
export function useRatingControls(id: string, slug: string) {
  const qc = useQueryClient();
  const settled = {
    onSuccess: () => {
      invalidateContest(qc, id);
      void qc.invalidateQueries({ queryKey: ["contests", "rating", slug] });
      void qc.invalidateQueries({ queryKey: ["contests", "standings", slug] });
      void qc.invalidateQueries({ queryKey: ["contests", "contest"] });
    },
  };

  return {
    setRated: useMutation({ mutationFn: (rated: boolean) => api.setContestRated(id, rated), ...settled }),
    apply: useMutation({ mutationFn: () => api.applyContestRating(id), ...settled }),
    replay: useMutation({ mutationFn: () => api.replayContestRating(id), ...settled }),
  };
}

/**
 * The solve-time report (`EX-ANM01`, `docs/29` §6.2).
 *
 * `retry: false` for the reason the judge-authoring reads give: a 403 here is a
 * decision about who is asking, not a blip, and retrying it three times only
 * delays the refusal the panel is about to render.
 */
/**
 * `EX-RDY01`: "Ready for the day". Under the `contest-platform` prefix, so a
 * console write (`invalidateContest`) asks it again; never retried, never polled.
 */
export function useContestReadiness(id: string, enabled = true) {
  return useQuery({
    queryKey: ["contest-platform", "readiness", id] as const,
    queryFn: () => api.fetchContestReadiness(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

export function useContestTiming(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerTiming(id),
    queryFn: () => api.fetchContestTiming(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

/**
 * The cluster review with its authorship column (`EX-AUT06`, `docs/29` §6.2).
 *
 * `retry: false` for the same reason the timing report has it: a contestant who
 * reaches this by guessing a URL is refused 403, and retrying a refusal three
 * times is three refusals.
 */
export function useContestSimilarity(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerSimilarity(id),
    queryFn: () => api.fetchContestSimilarity(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

/**
 * `EX-CMP01`: one pair side by side. Under the cluster review's key, so its
 * "Run again" refreshes an open pair too; `retry: false` for the same reason.
 */
export function useContestSimilarityPair(id: string, key: ContestSimilarityPairKey | null) {
  return useQuery({
    queryKey: contestKeys.organizerSimilarityPair(id, key?.problemId ?? "", key?.left ?? "", key?.right ?? ""),
    queryFn: () => api.fetchContestSimilarityPair(id, key!),
    enabled: !!id && key !== null,
    retry: false,
  });
}

/**
 * The whole round's integrity report (`EX-INT04`, `docs/29` §6.2).
 *
 * `retry: false` for the reason the timing report has it: a contestant who
 * reaches this by guessing a URL is refused 403, and retrying a refusal three
 * times is three refusals.
 */
/**
 * `EX-EDO01`: how often the console re-reads a live round's integrity. Only
 * while the round is open and the tab is in front (React Query pauses an
 * interval in a background tab), so a finished round costs nothing.
 */
export const LIVE_INTEGRITY_REPORT_MS = 30_000;
export const LIVE_INTEGRITY_TIMELINE_MS = 10_000;
export const LIVE_HOLDS_MS = 10_000;

export function useContestIntegrityReport(id: string, enabled = true, live = false) {
  return useQuery({
    queryKey: contestKeys.organizerIntegrityReport(id),
    queryFn: () => api.fetchContestIntegrityReport(id),
    enabled: enabled && !!id,
    retry: false,
    refetchInterval: live ? LIVE_INTEGRITY_REPORT_MS : false,
  });
}

/** `EX-INT07`: one entry's timeline, fetched only when a row is opened; live while the round runs (`EX-EDO01`). */
export function useContestIntegrityTimeline(id: string, participantId: string | null, live = false) {
  return useQuery({
    queryKey: contestKeys.organizerIntegrityTimeline(id, participantId ?? ""),
    queryFn: () => api.fetchContestIntegrityTimeline(id, participantId!),
    enabled: !!id && !!participantId,
    retry: false,
    refetchInterval: live ? LIVE_INTEGRITY_TIMELINE_MS : false,
  });
}

/** `EX-EDO01`: who is waiting to be let back in, polled while the round is open. */
export function useContestHolds(id: string, enabled = true, live = false) {
  return useQuery({
    queryKey: contestKeys.organizerHolds(id),
    queryFn: () => api.fetchContestHolds(id),
    enabled: enabled && !!id,
    retry: false,
    refetchInterval: live ? LIVE_HOLDS_MS : false,
  });
}

/** `EX-EDO01`: let a held contestant back in; the list and the report refresh after. */
export function useReleaseContestHold(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { participantId: string; userId: string }) =>
      api.releaseContestHold(id, input.participantId, input.userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contestKeys.organizerIntegrityReport(id) });
    },
  });
}

/** One entry's integrity record, fetched only when a moderator opens it. */
export function useContestIntegrityRecord(id: string, participantId: string | null) {
  return useQuery({
    queryKey: contestKeys.organizerIntegrityRecord(id, participantId ?? ""),
    queryFn: () => api.fetchContestIntegrityRecord(id, participantId!),
    enabled: !!id && !!participantId,
    retry: false,
  });
}

/**
 * `EX-WSP01`: the stream is immutable once the round has ended, so it never
 * goes stale; `retry: false` because the empty case is a 404 and the running
 * case a 409, and neither gets better by asking again.
 */
export function useWorkspaceSnapshots(
  id: string,
  participantId: string | null,
  problemId: string | null,
) {
  return useQuery({
    queryKey: contestKeys.organizerSnapshots(id, participantId ?? "", problemId ?? ""),
    queryFn: () => api.fetchWorkspaceSnapshots(id, participantId!, problemId!),
    enabled: !!id && !!participantId && !!problemId,
    staleTime: Infinity,
    retry: false,
  });
}

/** Run the detectors on demand, then replace the cached report with the fresh one. */
/**
 * `EX-RPT01`: the Round Report. Read once the round has ended; a refusal
 * before that is the panel's own note, so it is not retried.
 */
/** `EX-HAL01`: the hall guide card's state and, once accepted, the guide. */
export function useContestHallGuide(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerHallGuide(id),
    queryFn: () => api.fetchContestHallGuide(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

export function useRequestContestHallGuide(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note?: string) => api.requestContestHallGuide(id, note),
    onSuccess: (data) => qc.setQueryData(contestKeys.organizerHallGuide(id), data),
  });
}

/** `EX-VEN01`: the venue lock as the console edits it. */
export function useContestVenueLock(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerVenueLock(id),
    queryFn: () => api.fetchContestVenueLock(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

/** Saves the whole lock; the panel shows the change at once and takes it back on a refusal. */
export function useSaveContestVenueLock(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContestVenueLockInput) => api.saveContestVenueLock(id, input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: contestKeys.organizerVenueLock(id) });
      const before = qc.getQueryData<ContestVenueLock>(contestKeys.organizerVenueLock(id));
      if (before) qc.setQueryData(contestKeys.organizerVenueLock(id), { ...before, ...input });
      return { before };
    },
    onError: (_error, _input, context) => {
      if (context?.before) qc.setQueryData(contestKeys.organizerVenueLock(id), context.before);
    },
    onSuccess: (data) => qc.setQueryData(contestKeys.organizerVenueLock(id), data),
  });
}

/** The Refused card: read again while the console is open, since a hall refuses in bursts. */
export function useContestVenueRefusals(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerVenueRefusals(id),
    queryFn: () => api.fetchContestVenueRefusals(id),
    enabled: enabled && !!id,
    refetchInterval: 20_000,
    retry: false,
  });
}

export function useContestVenueLetIn(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, letIn }: { userId: string; letIn: boolean }) => api.setContestVenueLetIn(id, userId, letIn),
    onSettled: () => qc.invalidateQueries({ queryKey: contestKeys.organizerVenueRefusals(id) }),
  });
}

/** The projector's code: read every five seconds, and again the moment it changes. */
export function useContestVenueRoomCode(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerVenueRoomCode(id),
    queryFn: () => api.fetchContestVenueRoomCode(id),
    enabled: enabled && !!id,
    refetchInterval: 5_000,
    refetchIntervalInBackground: true,
    retry: 1,
  });
}

export function useContestReport(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerReport(id),
    queryFn: () => api.fetchContestReport(id),
    enabled: enabled && !!id,
    retry: false,
  });
}

export function useDraftContestReportSummary(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idempotencyKey: string) => api.draftContestReportSummary(id, idempotencyKey),
    onSettled: () => void qc.invalidateQueries({ queryKey: contestKeys.organizerReport(id) }),
  });
}

export function useSaveContestReportSummary(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { en: string; bn: string; includeInPdf: boolean }) => api.saveContestReportSummary(id, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contestKeys.organizerReport(id) }),
  });
}

/** `EX-OVL01`: the live overlay's links and switches. */
export function useContestBroadcast(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerBroadcast(id),
    queryFn: () => api.fetchContestBroadcast(id),
    enabled,
    retry: false,
  });
}

/** Make the links, or replace them; the answer is the section's new state. */
export function useMakeContestBroadcastKey(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.makeContestBroadcastKey(id),
    onSuccess: (broadcast) => qc.setQueryData(contestKeys.organizerBroadcast(id), broadcast),
  });
}

/** `EX-SPN01`: the sponsors, the places they print and the page's look. */
export function useContestBrand(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerBrand(id),
    queryFn: () => api.fetchContestBrand(id),
    enabled,
    retry: false,
  });
}

/** Save the whole block; the contest page reads it again on its next visit. */
export function useSaveContestBrand(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (brand: ContestBrandInput) => api.saveContestBrand(id, brand),
    onSuccess: (read) => {
      qc.setQueryData(contestKeys.organizerBrand(id), read);
      void qc.invalidateQueries({ queryKey: contestKeys.organizerSponsorSheet(id) });
    },
  });
}

/** `EX-TAL01`: the reader's own Talent Pass on a round whose sponsors look for talent. */
export function useMyTalentPass(slug: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.talentPass(slug),
    queryFn: () => api.fetchMyTalentPass(slug),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

/** Give or withdraw; the answer replaces the cached pass at once. */
export function useSetMyTalentPass(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (consent: boolean) => api.setMyTalentPass(slug, consent),
    onSuccess: (pass) => qc.setQueryData(contestKeys.talentPass(slug), pass),
  });
}

/** `EX-SPN01`: the sponsor sheet, after the end. */
export function useContestSponsorSheet(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerSponsorSheet(id),
    queryFn: () => api.fetchContestSponsorSheet(id),
    enabled,
    retry: false,
  });
}

/* ── `EX-INS01`: institutions and the season ──────────────────────────────── */

/** The contest page's Institutions tab; only asked for once the round has ended. */
export function useContestInstitutions(slug: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.institutions(slug),
    queryFn: () => api.fetchContestInstitutions(slug),
    enabled: enabled && !!slug,
    retry: false,
    staleTime: 30_000,
  });
}

export function usePublicSeason(slug: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.publicSeason(slug),
    queryFn: () => api.fetchPublicSeason(slug),
    enabled: enabled && !!slug,
    retry: false,
    staleTime: 30_000,
  });
}

export function useMySeasons(enabled = true) {
  return useQuery({ queryKey: contestKeys.mySeasons(), queryFn: api.fetchMySeasons, enabled, retry: false });
}

export function useSeason(id: string | null) {
  return useQuery({
    queryKey: contestKeys.season(id ?? ""),
    queryFn: () => api.fetchSeason(id!),
    enabled: !!id,
    retry: false,
  });
}

export function useContestSeasons(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.contestSeasons(id),
    queryFn: () => api.fetchContestSeasons(id),
    enabled,
    retry: false,
  });
}

/** Every season write moves the console's lists; the saved season is its own answer. */
export function useSeasonWrites(contestId: string) {
  const qc = useQueryClient();
  const settle = (season?: EditorSeason) => {
    if (season) qc.setQueryData(contestKeys.season(season.id), season);
    void qc.invalidateQueries({ queryKey: contestKeys.mySeasons() });
    void qc.invalidateQueries({ queryKey: contestKeys.contestSeasons(contestId) });
  };
  const create = useMutation({ mutationFn: (input: SeasonInput) => api.createSeason(input), onSuccess: settle });
  const save = useMutation({
    mutationFn: ({ id, input }: { id: string; input: SeasonInput }) => api.saveSeason(id, input),
    onSuccess: settle,
  });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteSeason(id), onSuccess: () => settle() });
  return { create, save, remove };
}

/** `EX-CLI01`: the ICPC feed card. */
export function useContestFeed(id: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerFeed(id),
    queryFn: () => api.fetchContestFeed(id),
    enabled,
    retry: false,
  });
}

/** Make or replace the token. The card is cached without the token; the caller holds it once. */
export function useMakeContestFeedToken(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.makeContestFeedToken(id),
    onSuccess: ({ secret, ...feed }) => {
      void secret; // never cached: only the caller holds it, once
      qc.setQueryData(contestKeys.organizerFeed(id), feed);
    },
  });
}

export function useRevokeContestFeedToken(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.revokeContestFeedToken(id),
    onSuccess: (feed) => qc.setQueryData(contestKeys.organizerFeed(id), feed),
  });
}

export function useSetContestFeedFinalResults(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (finalResults: boolean) => api.setContestFeedFinalResults(id, finalResults),
    onSuccess: (feed) => qc.setQueryData(contestKeys.organizerFeed(id), feed),
  });
}

export function useSetContestBroadcastLayers(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (layers: Partial<Record<LiveLayer, boolean>>) => api.setContestBroadcastLayers(id, layers),
    onSuccess: (broadcast) => qc.setQueryData(contestKeys.organizerBroadcast(id), broadcast),
  });
}

export function useRecomputeContestTiming(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.recomputeContestTiming(id),
    onSuccess: (report) => {
      qc.setQueryData(contestKeys.organizerTiming(id), report);
      // `EX-AUT06`: one Recompute runs both families, so the cluster review's
      // authorship column and every open record are stale the moment it returns.
      void qc.invalidateQueries({ queryKey: contestKeys.organizerSimilarity(id) });
      void qc.invalidateQueries({ queryKey: ["contest-platform", "integrity", id] });
    },
  });
}

/** `EX-STP01`: whose page a leaked statement came from. Nothing to cache: each paste is its own. */
export function useTraceStatementDecoy(id: string) {
  return useMutation({ mutationFn: (text: string) => api.traceStatementDecoy(id, text) });
}

/* ── The print queue (`EX-ONS02`, `docs/29` §6.2) ────────────────────────── */

/** A slip the station still has to act on. */
const PRINT_JOB_ACTIVE: ReadonlySet<ContestPrintStatus> = new Set(["queued", "printing", "printed"]);

/**
 * The entry's own slips. Polled every fifteen seconds only while one is still
 * on its way, so a workspace with nothing printing costs nothing.
 */
export function useOwnPrintJobs(slug: string, enabled = true) {
  return useQuery({
    queryKey: contestKeys.printJobs(slug),
    queryFn: () => api.fetchOwnPrintJobs(slug),
    enabled: enabled && !!slug,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.jobs.some((job) => PRINT_JOB_ACTIVE.has(job.status)) ? 15_000 : false,
  });
}

export function useRequestPrint(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RequestPrintInput) => api.requestContestPrint(slug, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contestKeys.printJobs(slug) });
    },
  });
}

/** The organizer's queue with its counts. Read only where the console has the kit. */
export function usePrintQueue(id: string, query: { roomId?: string; status?: ContestPrintStatus }, enabled = true) {
  return useQuery({
    queryKey: contestKeys.organizerPrint(id, query.roomId, query.status),
    queryFn: () => api.fetchPrintQueue(id, query),
    enabled: enabled && !!id,
    retry: false,
    refetchInterval: 15_000,
  });
}

/** One room's screen, polled every five seconds: the operator watches it, not the contestant. */
export function usePrintStation(id: string, roomId: string) {
  return useQuery({
    queryKey: contestKeys.organizerPrintStation(id, roomId),
    queryFn: () => api.fetchPrintStation(id, roomId),
    enabled: !!id && !!roomId,
    retry: false,
    refetchInterval: 5_000,
  });
}

export function useTransitionPrintJob(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { jobId: string; status: Exclude<ContestPrintStatus, "queued"> }) =>
      api.transitionPrintJob(id, input.jobId, input.status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contest-platform", "print-station", id] });
      void qc.invalidateQueries({ queryKey: ["contest-platform", "print", id] });
    },
  });
}

/* ── The balloons (`EX-ONS03`, `docs/29` §6.2) ───────────────────────────── */

/** The board and the runner page, polled every five seconds: a runner is standing next to it. */
/**
 * `EX-DAY05`: the reveal's board, read once. Never refetched behind the
 * operator's back: the page plays this one answer press by press, and re-reads
 * it itself only before the last press, to compare the final table's digest.
 */
export function useContestReveal(id: string) {
  return useQuery({
    queryKey: contestKeys.organizerReveal(id),
    queryFn: () => api.fetchContestReveal(id),
    enabled: !!id,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * `EX-DAY05`: the reveal read again, fresh, before its last press. A mutation
 * rather than the query above, so the board being played is never replaced:
 * the page only compares the new answer's digest with the one it played.
 */
export function useRecheckContestReveal(id: string) {
  return useMutation({ mutationFn: () => api.fetchContestReveal(id) });
}

export function useBalloonBoard(
  id: string,
  query: { roomId?: string; status?: ContestBalloonStatus },
  enabled = true,
) {
  return useQuery({
    queryKey: contestKeys.organizerBalloons(id, query.roomId, query.status),
    queryFn: () => api.fetchBalloonBoard(id, query),
    enabled: enabled && !!id,
    retry: false,
    refetchInterval: 5_000,
  });
}

export function useDeliverBalloon(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (balloonId: string) => api.deliverBalloon(id, balloonId),
    onSettled: () => {
      // A refused move (somebody else's tap landed first) refreshes the list too.
      void qc.invalidateQueries({ queryKey: ["contest-platform", "balloons", id] });
    },
  });
}
